import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatResidentParkingUsesChangeLocationState} from './NatResidentApplicationInterface'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {NatPutParkingUsesChangeRequest, NatPutParkingUsesChangeResponse} from '../repository/NatPutParkingUsesChange'
import NatResidentParkingUsesChangeForm from './resident/NatResidentParkingUsesChangeForm'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentParkingUsesChangeLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    useArea: Nullable<string>
    changeType: Nullable<string>
    changeStartDate: Nullable<string>
    changeEndDate: Nullable<string>
    userFamilyName: Nullable<string>
    userFirstName: Nullable<string>
    userAttribute: Nullable<string>
    ownerFamilyName: Nullable<string>
    ownerFirstName: Nullable<string>
    carBodyShape: Nullable<string>
    carName: Nullable<string>
    carRegistrationNumber: Nullable<string>
    engineSize: Nullable<string>
    length: Nullable<string>
    height: Nullable<string>
    width: Nullable<string>
    vehicleWeight: Nullable<string>
    vehicleTotalWeight: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentParkingUsesChangeInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            useArea: null,
            changeType: null,
            changeStartDate: null,
            changeEndDate: null,
            userFamilyName: null,
            userFirstName: null,
            userAttribute: null,
            ownerFamilyName: null,
            ownerFirstName: null,
            carBodyShape: null,
            carName: null,
            carRegistrationNumber: null,
            engineSize: null,
            length: null,
            height: null,
            width: null,
            vehicleWeight: null,
            vehicleTotalWeight: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultUseArea = applyInfo?.useArea ?? null
        let defaultChangeType = applyInfo?.changeType ?? null
        let defaultChangeStartDate = applyInfo?.changeStartDate ?? null
        let defaultChangeEndDate = applyInfo?.changeEndDate ?? null
        let defaultUserFamilyName = applyInfo?.userFamilyName ?? null
        let defaultUserFirstName = applyInfo?.userFirstName ?? null
        let defaultUserAttribute = applyInfo?.userAttribute ?? null
        let defaultOwnerFamilyName = applyInfo?.ownerFamilyName ?? null
        let defaultOwnerFirstName = applyInfo?.ownerFirstName ?? null
        let defaultCarBodyShape = applyInfo?.carBodyShape ?? null
        let defaultCarName = applyInfo?.carName ?? null
        let defaultCarRegistrationNumber = applyInfo?.carRegistrationNumber ?? null
        let defaultEngineSize = applyInfo?.engineSize ?? null
        let defaultLength = applyInfo?.length ?? null
        let defaultHeight = applyInfo?.height ?? null
        let defaultWidth = applyInfo?.width ?? null
        let defaultVehicleWeight = applyInfo?.vehicleWeight ?? null
        let defaultVehicleTotalWeight = applyInfo?.vehicleTotalWeight ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const parkingUsesChangeDetail = await this.props.asyncCallFunction(
                    applicationRepo.getParkingUsesChangeForResident.bind(applicationRepo, condominiumCd, applyId),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (parkingUsesChangeDetail?.details) {
                    defaultUseArea = parkingUsesChangeDetail.details.useArea
                    defaultChangeType = parkingUsesChangeDetail.details.changeType
                    defaultChangeStartDate = parkingUsesChangeDetail.details.changeStartDate
                    defaultChangeEndDate = parkingUsesChangeDetail.details.changeEndDate
                    defaultUserFamilyName = parkingUsesChangeDetail.details.userFamilyName
                    defaultUserFirstName = parkingUsesChangeDetail.details.userFirstName
                    defaultUserAttribute = parkingUsesChangeDetail.details.userAttribute
                    defaultOwnerFamilyName = parkingUsesChangeDetail.details.ownerFamilyName
                    defaultOwnerFirstName = parkingUsesChangeDetail.details.ownerFirstName
                    defaultCarBodyShape = parkingUsesChangeDetail.details.carBodyShape
                    defaultCarName = parkingUsesChangeDetail.details.carName
                    defaultCarRegistrationNumber = parkingUsesChangeDetail.details.carRegistrationNumber
                    defaultEngineSize = parkingUsesChangeDetail.details.engineSize
                    defaultLength = parkingUsesChangeDetail.details.length
                    defaultHeight = parkingUsesChangeDetail.details.height
                    defaultWidth = parkingUsesChangeDetail.details.width
                    defaultVehicleWeight = parkingUsesChangeDetail.details.vehicleWeight
                    defaultVehicleTotalWeight = parkingUsesChangeDetail.details.vehicleTotalWeight
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            useArea: defaultUseArea,
            changeType: defaultChangeType,
            changeStartDate: defaultChangeStartDate,
            changeEndDate: defaultChangeEndDate,
            userFamilyName: defaultUserFamilyName,
            userFirstName: defaultUserFirstName,
            userAttribute: defaultUserAttribute,
            ownerFamilyName: defaultOwnerFamilyName,
            ownerFirstName: defaultOwnerFirstName,
            carBodyShape: defaultCarBodyShape,
            carName: defaultCarName,
            carRegistrationNumber: defaultCarRegistrationNumber,
            engineSize: defaultEngineSize,
            length: defaultLength,
            height: defaultHeight,
            width: defaultWidth,
            vehicleWeight: defaultVehicleWeight,
            vehicleTotalWeight: defaultVehicleTotalWeight,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutParkingUsesChangeRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutParkingUsesChangeResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyParkingUsesChange.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentParkingUsesChangeLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        useArea: request.useArea,
                        changeType: request.changeType,
                        changeStartDate: request.changeStartDate,
                        changeEndDate: request.changeEndDate,
                        userFamilyName: request.userFamilyName,
                        userFirstName: request.userFirstName,
                        userAttribute: request.userAttribute,
                        ownerFamilyName: request.ownerFamilyName,
                        ownerFirstName: request.ownerFirstName,
                        carBodyShape: request.carBodyShape,
                        carName: request.carName,
                        carRegistrationNumber: request.carRegistrationNumber,
                        engineSize: request.engineSize,
                        length: request.length,
                        height: request.height,
                        width: request.width,
                        vehicleWeight: request.vehicleWeight,
                        vehicleTotalWeight: request.vehicleTotalWeight,
                    },
                    applyId: this.props.location.state.applyId,
                }
                this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/applications/parkingUsesChange/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/parkingUsesChange/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/parkingUsesChange/agreement`
                    }>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    自動車変更届
                </PageTitle>
                <Box height={'30px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentParkingUsesChangeForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentParkingUsesChangeInputPage)
